/**
 * #1071 — how often the highest-volume rule answers on a real board (or in Game
 * Detail, switched on), and why it does not. Page-lifetime, this tab only, cleared at sign-out and on any
 * account/organization/role change (`authStore`), like G1 Health's "Game-state
 * comparisons". Nothing is sent anywhere.
 *
 * Each spread/total row of a game is counted ONCE until the count is cleared
 * (remembered for the last 16,384 rows; an older row can be counted again):
 * its first final outcome, on the board or in Game Detail, whichever settles it
 * first. `active` counts rows the switch let the rule seed; `shadow` counts
 * what the rule would have seeded while the board kept today's 50¢ default.
 * Recording is idempotent, so a repeated pass (a StrictMode re-run, a
 * remount, a row leaving and returning) never counts a row twice within that
 * bound.
 *
 * G1 Health's "Initial spread/total line" section shows it
 * (`G1Health/initialLineHealth.tsx`), reading through
 * `subscribeInitialLineTally` and `initialLineTallySnapshot`.
 */
import type { InitialLineOutcome } from '../views/SpreadsTotals/initialLine';

type Kind = 'spread' | 'total';
export type InitialLineMode = 'active' | 'shadow';
/** The role of the feed that owned the row's sport when its wait ran out;
 *  `unknown` when the store named no owner. */
export type InitialLineOwnerRole = 'firehose' | 'core' | 'edge' | 'unknown';
/** `touched`: an expand, pick, ticket click or focus link chose the line
 *  before the rule's answer was final, so the rule never seeded the row. */
export type InitialLineTallyOutcome = InitialLineOutcome | { kind: 'touched' };
/** Every outcome name the count can hold (the part after `<mode> `). */
export type InitialLineOutcomeName =
  | `volume:${'at-50c' | 'moved'}`
  | `fallback:${Extract<InitialLineOutcome, { kind: 'fallback' }>['reason']}`
  | `limit:${InitialLineOwnerRole}`
  | 'touched';

/** What a display reads: the count, read-only all the way down. */
export type InitialLineTallyView = { readonly counts: Readonly<Record<string, number>>; readonly reused: number };

export interface InitialLineTally {
  /** `<mode> <outcome>` → rows, e.g. `active volume:moved`, `shadow fallback:none-traded`,
   *  `active limit:core`. */
  counts: Record<string, number>;
  /** Times the board reused a seeded default when it mounted (Back, a tab
   *  switch, or a line Game Detail seeded first), once per row per mount. */
  reused: number;
}

const MAX_DECIDED = 16_384;
const decided = new Set<string>();
const counts = new Map<string, number>();
let reused = 0;
// What a display reads (`useSyncExternalStore`): one object per state, rebuilt
// on the first read after a change.
let snapshot: InitialLineTallyView | null = null;
const listeners = new Set<() => void>();
function changed(): void {
  snapshot = null;
  for (const listener of listeners) listener();
}
const key = (scope: string, kind: Kind, event: string) => JSON.stringify([scope, kind, event]);

function label(outcome: InitialLineTallyOutcome, role: InitialLineOwnerRole): InitialLineOutcomeName {
  switch (outcome.kind) {
    case 'volume': return outcome.atFifty ? 'volume:at-50c' : 'volume:moved';
    case 'fallback': return `fallback:${outcome.reason}`;
    case 'limit': return `limit:${role}`;
    case 'touched': return 'touched';
  }
}

export function initialLineDecided(scope: string | null, kind: Kind, event: string): boolean {
  return scope != null && decided.has(key(scope, kind, event));
}

/** Counts `outcome` for this row unless the row was already counted. */
export function recordInitialLine(
  scope: string | null, kind: Kind, event: string,
  mode: InitialLineMode, outcome: InitialLineTallyOutcome, role: InitialLineOwnerRole = 'unknown',
): void {
  if (!scope) return;
  const id = key(scope, kind, event);
  if (decided.has(id)) return;
  decided.add(id);
  while (decided.size > MAX_DECIDED) decided.delete(decided.values().next().value!);
  const name = `${mode} ${label(outcome, role)}`;
  counts.set(name, (counts.get(name) ?? 0) + 1);
  changed();
}

export function noteInitialLineReused(): void {
  reused++;
  changed();
}

export function readInitialLineTally(): InitialLineTally {
  return { counts: Object.fromEntries([...counts].sort(([a], [b]) => a.localeCompare(b))), reused };
}

/** The count for a display: the same object until the count next changes.
 *  Read-only; `readInitialLineTally` is the copy to change. */
export function initialLineTallySnapshot(): InitialLineTallyView {
  snapshot ??= readInitialLineTally();
  return snapshot;
}

/** Calls `listener` after every change, including a clear. Returns the
 *  unsubscribe. */
export function subscribeInitialLineTally(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function resetInitialLineTally(): void {
  decided.clear();
  counts.clear();
  reused = 0;
  changed();
}
